import { type ReactNode, type ComponentType } from 'react';
import { cn } from '@/lib/utils';
import { FileQuestion, Inbox, Search, FolderOpen, AlertCircle } from 'lucide-react';
import { Button } from './Button';

export interface EmptyStateProps {
  icon?: ReactNode;
  illustration?: ComponentType<{ className?: string; primaryColor?: string }>;
  illustrationColor?: string;
  title: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
  };
  secondaryAction?: {
    label: string;
    onClick: () => void;
  };
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'search' | 'inbox' | 'folder' | 'error';
}

const EmptyState = ({
  icon,
  illustration: Illustration,
  illustrationColor,
  title,
  description,
  action,
  secondaryAction,
  className,
  size = 'md',
  variant = 'default',
}: EmptyStateProps) => {
  const sizes = {
    sm: {
      container: 'py-6 px-4',
      iconWrapper: 'p-2.5',
      icon: 'h-5 w-5',
      title: 'text-sm',
      description: 'text-xs',
    },
    md: {
      container: 'py-10 px-6',
      iconWrapper: 'p-3',
      icon: 'h-6 w-6',
      title: 'text-base',
      description: 'text-sm',
    },
    lg: {
      container: 'py-14 px-8',
      iconWrapper: 'p-4',
      icon: 'h-8 w-8',
      title: 'text-lg',
      description: 'text-sm',
    },
  };

  const defaultIcons = {
    default: <FileQuestion className={cn('text-secondary-400', sizes[size].icon)} />,
    search: <Search className={cn('text-secondary-400', sizes[size].icon)} />,
    inbox: <Inbox className={cn('text-secondary-400', sizes[size].icon)} />,
    folder: <FolderOpen className={cn('text-secondary-400', sizes[size].icon)} />,
    error: <AlertCircle className={cn('text-danger-400', sizes[size].icon)} />,
  };

  const iconBgColors = {
    default: 'bg-gray-100 dark:bg-secondary-800',
    search: 'bg-gray-100 dark:bg-secondary-800',
    inbox: 'bg-gray-100 dark:bg-secondary-800',
    folder: 'bg-gray-100 dark:bg-secondary-800',
    error: 'bg-danger-100 dark:bg-danger-500/20',
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center',
        sizes[size].container,
        className
      )}
    >
      {Illustration ? (
        <Illustration
          className={cn(
            'mb-4',
            size === 'sm' && 'h-20 w-20',
            size === 'md' && 'h-32 w-32',
            size === 'lg' && 'h-40 w-40'
          )}
          primaryColor={illustrationColor}
        />
      ) : (
        <div className={cn(
          'mb-3 rounded-xl',
          iconBgColors[variant],
          sizes[size].iconWrapper
        )}>
          {icon || defaultIcons[variant]}
        </div>
      )}
      <h3 className={cn(
        'font-medium text-gray-900 dark:text-secondary-100',
        sizes[size].title
      )}>
        {title}
      </h3>
      {description && (
        <p className={cn(
          'mt-1 max-w-xs text-gray-500 dark:text-secondary-400',
          sizes[size].description
        )}>
          {description}
        </p>
      )}
      {(action || secondaryAction) && (
        <div className="mt-4 flex gap-2">
          {secondaryAction && (
            <Button
              variant="outline"
              size={size === 'sm' ? 'sm' : 'md'}
              onClick={secondaryAction.onClick}
            >
              {secondaryAction.label}
            </Button>
          )}
          {action && (
            <Button
              variant="primary"
              size={size === 'sm' ? 'sm' : 'md'}
              onClick={action.onClick}
            >
              {action.label}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// No Results variant for search
const NoResults = ({
  searchTerm,
  onClear,
  className,
}: {
  searchTerm?: string;
  onClear?: () => void;
  className?: string;
}) => (
  <EmptyState
    variant="search"
    title="No results found"
    description={
      searchTerm
        ? `No results for "${searchTerm}". Try adjusting your search.`
        : 'No results match your current filters.'
    }
    action={onClear ? { label: 'Clear filters', onClick: onClear } : undefined}
    className={className}
  />
);

// No Data variant
const NoData = ({
  title = 'No data yet',
  description,
  action,
  className,
}: {
  title?: string;
  description?: string;
  action?: { label: string; onClick: () => void };
  className?: string;
}) => (
  <EmptyState
    variant="inbox"
    title={title}
    description={description}
    action={action}
    className={className}
  />
);

export { EmptyState, NoResults, NoData };
